import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Slider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  VolumeUp as VolumeUpIcon,
  CloudUpload as UploadIcon,
  MusicNote as MusicNoteIcon,
  People as PeopleIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import Header from "../Header";
import { playSound, resolveAudioUrl } from "../../../utils/gameAudio";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const defaultSound = {
  name: "",
  image_url: "",
  audio_url: "",
  volume: 1.0,
  enabled: true,
};

const SoundPalette = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [sounds, setSounds] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSound, setEditingSound] = useState(null);
  const [formData, setFormData] = useState(defaultSound);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [adminEnabled, setAdminEnabled] = useState(true);
  const [userEnabled, setUserEnabled] = useState(false);
  const audioRef = useRef(null);

  const getAuthToken = () => localStorage.getItem("auth_token");

  const fetchSounds = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/sound-palette`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || "Failed to load sounds");
        return;
      }
      setSounds(data.sounds || []);
    } catch (err) {
      console.error("fetchSounds error", err);
      setError("Failed to load sounds");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSounds();
    return () => stopSound();
  }, []);

  const stopSound = () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch {
        /* ignore */
      }
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const handlePlaySound = async (sound) => {
    if (!adminEnabled && !userEnabled) {
      return;
    }

    stopSound();

    try {
      // Always hit play endpoint when User is on (broadcast) or to validate sound.
      // Admin hears locally only when Admin switch is enabled.
      const res = await fetch(`${API_BASE}/api/sound-palette/${sound.id}/play`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          play_for_admin: adminEnabled,
          play_for_user: userEnabled,
        }),
      });
      const data = await res.json();

      if (!data.success || !data.sound) {
        setError(data.error || "Failed to play sound");
        return;
      }

      setPlayingId(sound.id);

      if (adminEnabled) {
        const audio = playSound(data.sound.audio_url, {
          volume: data.sound.volume ?? 1,
        });
        audioRef.current = audio;
        if (audio) {
          audio.addEventListener("ended", () => {
            setPlayingId(null);
            audioRef.current = null;
          });
        } else {
          setTimeout(() => setPlayingId(null), 800);
        }
      } else {
        // User-only: show playing state briefly without admin audio
        setTimeout(() => setPlayingId(null), 800);
      }
    } catch (err) {
      console.error("Error playing sound:", err);
      setError("Failed to play sound");
    }
  };

  const handleStopAll = async () => {
    stopSound();
    try {
      await fetch(`${API_BASE}/api/sound-palette/stop`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });
    } catch (err) {
      console.error("Error stopping sounds:", err);
    }
  };

  const handleOpenDialog = (sound = null) => {
    if (sound) {
      setEditingSound(sound);
      setFormData({
        name: sound.name,
        image_url: sound.image_url || "",
        audio_url: sound.audio_url || "",
        volume: sound.volume ?? 1,
        enabled: sound.enabled !== 0,
      });
    } else {
      setEditingSound(null);
      setFormData(defaultSound);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSound(null);
    setFormData(defaultSound);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append("image", file);

      const res = await fetch(`${API_BASE}/api/upload/sound-palette-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formDataUpload,
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || "Failed to upload image");
        return;
      }
      setFormData((prev) => ({ ...prev, image_url: data.imageUrl }));
    } catch (err) {
      console.error("Image upload error:", err);
      setError("Failed to upload image");
    } finally {
      setUploadingImage(false);
      if (event.target) event.target.value = "";
    }
  };

  const handleAudioUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingAudio(true);
      const formDataUpload = new FormData();
      formDataUpload.append("audio", file);

      const res = await fetch(`${API_BASE}/api/upload/sound-palette-audio`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formDataUpload,
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || "Failed to upload audio");
        return;
      }
      setFormData((prev) => ({ ...prev, audio_url: data.audioUrl }));
    } catch (err) {
      console.error("Audio upload error:", err);
      setError("Failed to upload audio");
    } finally {
      setUploadingAudio(false);
      if (event.target) event.target.value = "";
    }
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      setError("Name is required");
      return;
    }
    if (!formData.audio_url?.trim()) {
      setError("Audio URL is required");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const url = editingSound
        ? `${API_BASE}/api/sound-palette/${editingSound.id}`
        : `${API_BASE}/api/sound-palette`;

      const method = editingSound ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          name: formData.name,
          image_url: formData.image_url,
          audio_url: formData.audio_url,
          volume: formData.volume,
          enabled: formData.enabled ? 1 : 0,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || "Failed to save");
        return;
      }

      setSuccess(editingSound ? "Sound updated!" : "Sound created!");
      handleCloseDialog();
      fetchSounds();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Save error:", err);
      setError("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sound?")) return;

    try {
      const res = await fetch(`${API_BASE}/api/sound-palette/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || "Failed to delete");
        return;
      }

      setSuccess("Sound deleted!");
      fetchSounds();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Delete error:", err);
      setError("Failed to delete");
    }
  };

  const handleToggleEnabled = async (sound) => {
    try {
      const res = await fetch(`${API_BASE}/api/sound-palette/${sound.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ enabled: sound.enabled ? 0 : 1 }),
      });

      if (res.ok) {
        fetchSounds();
      }
    } catch (err) {
      console.error("Toggle error:", err);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#121210" }}>
      <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <VolumeUpIcon sx={{ fontSize: 36, color: "#E040FB" }} />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#fff" }}>
              Sound Palette
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
              Create sounds and play them for Admin and/or User
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<StopIcon />}
            onClick={handleStopAll}
            color="error"
            sx={{ mr: 1 }}
          >
            Stop
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSounds}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: "#E040FB", "&:hover": { bgcolor: "#B040CB" } }}
          >
            Create Sound
          </Button>
        </Box>

        {/* Global Admin / User switches */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "#1E1E1E",
            border: "1px solid #333",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#fff" }}>
              Playback Targets
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
              Enable who hears sounds when you click them
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={adminEnabled}
                  onChange={(e) => setAdminEnabled(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#2196F3" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#2196F3",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <PersonIcon sx={{ fontSize: 20, color: adminEnabled ? "#2196F3" : "#777" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 500 }}>Admin</Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={userEnabled}
                  onChange={(e) => setUserEnabled(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#4CAF50" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#4CAF50",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <PeopleIcon sx={{ fontSize: 20, color: userEnabled ? "#4CAF50" : "#777" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 500 }}>User</Typography>
                </Box>
              }
            />
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : sounds.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center", bgcolor: "#1E1E1E", color: "#fff" }}>
            <MusicNoteIcon sx={{ fontSize: 64, color: "#555", mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              No sounds yet
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", mb: 2 }}>
              Create your first sound to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ bgcolor: "#E040FB", "&:hover": { bgcolor: "#B040CB" } }}
            >
              Create Sound
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {sounds.map((sound) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={sound.id}>
                <Card
                  sx={{
                    bgcolor: sound.enabled ? "#1E1E1E" : "#151515",
                    border: playingId === sound.id ? "2px solid #E040FB" : "1px solid #333",
                    borderRadius: 2,
                    opacity: sound.enabled && (adminEnabled || userEnabled) ? 1 : 0.5,
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: sound.enabled && (adminEnabled || userEnabled) ? "#E040FB" : "#333",
                      transform: sound.enabled && (adminEnabled || userEnabled) ? "scale(1.02)" : "none",
                    },
                  }}
                >
                  <CardMedia
                    component="div"
                    onClick={() =>
                      sound.enabled &&
                      (adminEnabled || userEnabled) &&
                      handlePlaySound(sound)
                    }
                    sx={{
                      height: 120,
                      bgcolor: "#252525",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor:
                        sound.enabled && (adminEnabled || userEnabled)
                          ? "pointer"
                          : "default",
                      backgroundImage: sound.image_url
                        ? `url(${resolveAudioUrl(sound.image_url)})`
                        : "none",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      position: "relative",
                      "&:hover":
                        sound.enabled && (adminEnabled || userEnabled)
                          ? {
                              "&::after": {
                                content: '""',
                                position: "absolute",
                                inset: 0,
                                bgcolor: "rgba(224, 64, 251, 0.3)",
                              },
                            }
                          : {},
                    }}
                  >
                    {!sound.image_url && (
                      <MusicNoteIcon sx={{ fontSize: 48, color: "#444" }} />
                    )}
                    {playingId === sound.id && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: "rgba(0,0,0,0.5)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PlayIcon sx={{ fontSize: 48, color: "#E040FB" }} />
                      </Box>
                    )}
                  </CardMedia>

                  <CardContent sx={{ py: 1, px: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#fff",
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sound.name}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ justifyContent: "space-between", px: 1, pt: 0, pb: 1 }}>
                    <Box>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(sound)}
                          sx={{ color: "#999" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(sound.id)}
                          sx={{ color: "#f44336" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Tooltip title={sound.enabled ? "Enabled" : "Disabled"}>
                      <Switch
                        size="small"
                        checked={sound.enabled === 1 || sound.enabled === true}
                        onChange={() => handleToggleEnabled(sound)}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#E040FB",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#E040FB",
                          },
                        }}
                      />
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "#1E1E1E",
              color: "#fff",
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle sx={{ borderBottom: "1px solid #333" }}>
            {editingSound ? "Edit Sound" : "Create New Sound"}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: "rgba(255,255,255,0.7)" } }}
              InputProps={{ sx: { color: "#fff" } }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1, color: "rgba(255,255,255,0.7)" }}>
              Image (Optional)
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Image URL or upload"
                value={formData.image_url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, image_url: e.target.value }))
                }
                InputProps={{ sx: { color: "#fff" } }}
              />
              <Button
                variant="outlined"
                component="label"
                disabled={uploadingImage}
                sx={{ minWidth: 100 }}
              >
                {uploadingImage ? <CircularProgress size={20} /> : <UploadIcon />}
                <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
              </Button>
            </Box>
            {formData.image_url && (
              <Box
                sx={{
                  mb: 2,
                  width: 80,
                  height: 80,
                  borderRadius: 1,
                  overflow: "hidden",
                  border: "1px solid #333",
                }}
              >
                <img
                  src={resolveAudioUrl(formData.image_url)}
                  alt="Preview"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1, color: "rgba(255,255,255,0.7)" }}>
              Audio *
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Audio URL or upload"
                value={formData.audio_url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, audio_url: e.target.value }))
                }
                InputProps={{ sx: { color: "#fff" } }}
              />
              <Button
                variant="outlined"
                component="label"
                disabled={uploadingAudio}
                sx={{ minWidth: 100 }}
              >
                {uploadingAudio ? <CircularProgress size={20} /> : <UploadIcon />}
                <input type="file" accept="audio/*" hidden onChange={handleAudioUpload} />
              </Button>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1, color: "rgba(255,255,255,0.7)" }}>
              Volume
            </Typography>
            <Slider
              value={formData.volume}
              min={0}
              max={1}
              step={0.05}
              onChange={(_, v) => setFormData((prev) => ({ ...prev, volume: v }))}
              sx={{ color: "#E040FB", mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, enabled: e.target.checked }))
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#E040FB",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#E040FB",
                    },
                  }}
                />
              }
              label="Enabled"
            />
          </DialogContent>
          <DialogActions sx={{ borderTop: "1px solid #333", px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} sx={{ color: "#999" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{ bgcolor: "#E040FB", "&:hover": { bgcolor: "#B040CB" } }}
            >
              {saving ? <CircularProgress size={20} /> : editingSound ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default SoundPalette;
